import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useServiceReports } from "../../hooks/useServiceReports";
import { useUsers } from "../../hooks/useUsers";
import { exportToExcel } from "../../utils/exportExcel";
import {
  SERVICE_CATEGORIES,
  SERVICE_STATUSES,
  SERVICE_TYPES,
  buildFileName,
  buildReportRow,
  formatDateTime,
  getReportColumns,
  getServiceCategory,
} from "./utils/reportUtils";

const DATE_FORMAT = "YYYY-MM-DD";

const byId = (items) =>
  items.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

function ReportsList() {
  const { t } = useTranslation();
  const { users, clients, fetchUsers, fetchClients } = useUsers();
  const {
    schedules,
    isLoading,
    error,
    detailProgress,
    fetchSchedules,
    fetchDetails,
  } = useServiceReports();

  const [from, setFrom] = useState(
    moment().startOf("month").format(DATE_FORMAT),
  );
  const [to, setTo] = useState(moment().endOf("month").format(DATE_FORMAT));
  const [serviceTypes, setServiceTypes] = useState(["correctivo"]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [includeDetails, setIncludeDetails] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  useEffect(() => {
    fetchSchedules({ from, to });
  }, [from, to, fetchSchedules]);

  const clientsById = useMemo(() => byId(clients), [clients]);
  const usersById = useMemo(() => byId(users), [users]);

  const filteredSchedules = useMemo(() => {
    const term = search.trim().toLowerCase();

    return schedules.filter((schedule) => {
      if (
        serviceTypes.length &&
        !serviceTypes.includes(schedule.service_type)
      ) {
        return false;
      }
      if (
        categories.length &&
        !categories.includes(getServiceCategory(schedule.service_type))
      ) {
        return false;
      }
      if (statuses.length && !statuses.includes(schedule.status)) return false;
      if (!term) return true;

      const row = buildReportRow(schedule, { t, clientsById, usersById });
      return [
        row.id,
        row.client,
        row.technician,
        row.description,
        row.serviceType,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [
    schedules,
    serviceTypes,
    categories,
    statuses,
    search,
    clientsById,
    usersById,
    t,
  ]);

  const rows = useMemo(
    () =>
      filteredSchedules.map((schedule) =>
        buildReportRow(schedule, { t, clientsById, usersById }),
      ),
    [filteredSchedules, clientsById, usersById, t],
  );

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );

  const summaryByCategory = useMemo(
    () =>
      SERVICE_CATEGORIES.map((category) => ({
        ...category,
        total: filteredSchedules.filter(
          (schedule) =>
            getServiceCategory(schedule.service_type) === category.value,
        ).length,
      })),
    [filteredSchedules],
  );

  useEffect(() => {
    setPage(0);
  }, [serviceTypes, categories, statuses, search, from, to]);

  const applyPreset = (preset) => {
    const reference = moment();
    if (preset === "lastMonth") reference.subtract(1, "month");

    if (preset === "thisYear") {
      setFrom(reference.clone().startOf("year").format(DATE_FORMAT));
      setTo(reference.clone().endOf("year").format(DATE_FORMAT));
      return;
    }

    setFrom(reference.clone().startOf("month").format(DATE_FORMAT));
    setTo(reference.clone().endOf("month").format(DATE_FORMAT));
  };

  const handleExport = async () => {
    if (!rows.length) return;

    setIsExporting(true);
    try {
      const source = includeDetails
        ? await fetchDetails(filteredSchedules)
        : filteredSchedules;

      exportToExcel({
        fileName: buildFileName({ from, to }),
        sheetName: t("servicesReport"),
        columns: getReportColumns(t, { includeDetails }),
        rows: source.map((schedule) =>
          buildReportRow(schedule, { t, clientsById, usersById }),
        ),
      });
    } catch (err) {
      console.error("Error al exportar el informe", err);
    } finally {
      setIsExporting(false);
    }
  };

  const renderSelectedTypes = (selected) => (
    // selected.length === 0 ? (
    //   <em>{t("allServiceTypes")}</em>
    // ) : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {selected.map((value) => (
        <Chip
          key={value}
          size="small"
          label={t(
            SERVICE_TYPES.find((type) => type.value === value)?.labelKey ||
              value,
          )}
        />
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("servicesReport")}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("dateFrom")}
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("dateTo")}
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </Grid>

          {/* <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary">
              {t("serviceType")}
            </Typography>
            <Select
              multiple
              fullWidth
              size="small"
              displayEmpty
              value={serviceTypes}
              onChange={(event) => setServiceTypes(event.target.value)}
              renderValue={renderSelectedTypes}
            >
              {SERVICE_TYPES.filter((type) => type.value === "correctivo").map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Checkbox
                    size="small"
                    checked={serviceTypes.includes(type.value)}
                  />
                  <ListItemText primary={t(type.labelKey)} />
                </MenuItem>
              ))}
            </Select>
          </Grid> */}

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              {t("serviceCategory")}
            </Typography>
            <Select
              multiple
              fullWidth
              size="small"
              displayEmpty
              value={categories}
              onChange={(event) => setCategories(event.target.value)}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <em>{t("allCategories")}</em>
                ) : (
                  selected
                    .map((value) =>
                      t(
                        SERVICE_CATEGORIES.find((item) => item.value === value)
                          ?.labelKey || value,
                      ),
                    )
                    .join(", ")
                )
              }
            >
              {SERVICE_CATEGORIES.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  <Checkbox
                    size="small"
                    checked={categories.includes(category.value)}
                  />
                  <ListItemText primary={t(category.labelKey)} />
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              {t("status")}
            </Typography>
            <Select
              multiple
              fullWidth
              size="small"
              displayEmpty
              value={statuses}
              onChange={(event) => setStatuses(event.target.value)}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <em>{t("allStatuses")}</em>
                ) : (
                  selected.map((value) => t(value)).join(", ")
                )
              }
            >
              {SERVICE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox size="small" checked={statuses.includes(status)} />
                  <ListItemText primary={t(status)} />
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("searchServices")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" onClick={() => applyPreset("thisMonth")}>
                {t("thisMonth")}
              </Button>
              <Button size="small" onClick={() => applyPreset("lastMonth")}>
                {t("lastMonth")}
              </Button>
              <Button size="small" onClick={() => applyPreset("thisYear")}>
                {t("thisYear")}
              </Button>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => fetchSchedules({ from, to })}
              >
                {t("refresh")}
              </Button>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeDetails}
                  onChange={(event) => setIncludeDetails(event.target.checked)}
                />
              }
              label={t("includeFullDetail")}
            />
            <Button
              variant="contained"
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={isExporting || isLoading || !rows.length}
              onClick={handleExport}
            >
              {t("exportExcel")}
            </Button>
          </Grid>
        </Grid>

        {includeDetails && (
          <Typography variant="caption" color="text.secondary">
            {t("includeFullDetailHelp")}
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Chip color="primary" label={`${t("totalServices")}: ${rows.length}`} />
        {summaryByCategory.map((category) => (
          <Chip
            key={category.value}
            variant="outlined"
            label={`${t(category.labelKey)}: ${category.total}`}
          />
        ))}
      </Box>

      {detailProgress && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("loadingDetails")} {detailProgress.loaded}/{detailProgress.total}
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {t("reportLoadError")}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t("scheduledDate")}</TableCell>
              <TableCell>{t("serviceType")}</TableCell>
              <TableCell>{t("serviceCategory")}</TableCell>
              <TableCell>{t("status")}</TableCell>
              <TableCell>{t("description")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !rows.length && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t("noResults")}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              paginatedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{formatDateTime(row.startDate)}</TableCell>
                  <TableCell>{row.serviceType}</TableCell>
                  <TableCell>{row.serviceCategory}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.description}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
}

export default ReportsList;
